import {
    CalendarDaysIcon,
    ClipboardDocumentListIcon,
    ShoppingBagIcon,
} from '@heroicons/react/24/outline';

import type { ElementFil } from '../api/useFilConversation';
import { heureCourte } from '../helpers/conversation';
import { t } from '../i18n/langue';

import Text from './Text';

/**
 * Un élément de contexte dans le fil — réservation, formulaire, commande.
 *
 * **Intercalé à sa date, pas groupé en fin de fil.** Le fil raconte le séjour :
 * répondre sans savoir si la personne arrive ce soir ou est partie la semaine
 * dernière n'est pas tenable, et l'en-tête de #25 ne porte que le nom de l'hôtel.
 *
 * **Une ligne sobre, pas une fiche.** Elle dit *quoi* et *quand* — le détail vit au
 * bureau. C'est ce qui permet de garder ces trois sources sans transformer le fil
 * en tableau de bord ; les six types morts mesurés par #31 ne sont même pas
 * chargés.
 */
interface CarteContexteProps {
    element: Exclude<ElementFil, { genre: 'message' }>;
}

export default function CarteContexte({ element }: CarteContexteProps) {
    const { Icone, libelle } = decrire(element);

    return (
        <div className="mb-2 flex items-center gap-2 rounded-xl border border-border-color-light bg-white px-3 py-2">
            <Icone className="h-4 w-4 shrink-0 text-main-color" aria-hidden="true" />
            <Text as="p" variante="petit" ton="attenue" className="min-w-0 flex-1 truncate">
                {libelle}
            </Text>
            <Text as="p" variante="petit" ton="attenue" className="shrink-0">
                {heureCourte(element.date)}
            </Text>
        </div>
    );
}

function decrire(element: CarteContexteProps['element']) {
    switch (element.genre) {
        case 'reservation': {
            const r = element.reservation;
            const sejour = [r.date_arrival, r.date_departure].filter(Boolean).join(' → ');
            return {
                Icone: CalendarDaysIcon,
                libelle: sejour ? t('messagerie.contexte.reservationSejour', { sejour }) : t('messagerie.contexte.reservation'),
            };
        }
        case 'formulaire': {
            const nom = element.reponse.survey_name;
            return {
                Icone: ClipboardDocumentListIcon,
                libelle: nom ? t('messagerie.contexte.formulaireNomme', { nom }) : t('messagerie.contexte.formulaire'),
            };
        }
        case 'commande': {
            const total = element.commande.total_price;
            return {
                Icone: ShoppingBagIcon,
                libelle:
                    total != null
                        ? t('messagerie.contexte.commandeTotal', { total })
                        : t('messagerie.contexte.commande'),
            };
        }
    }
}
